/**
 * 在途监管平台 — 应用外壳（侧边栏 + 顶栏 + 角色切换）
 * 三端共用一套设计系统：/gov 政府端、/enterprise 企业端、/driver 司机端（移动布局）
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TOKEN_KEY, USER_KEY } from '@/api/http';
import type { User } from '@/types';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const govNav: NavItem[] = [
  { to: '/gov', label: '监管驾驶舱', icon: '📊' },
  { to: '/gov/vehicles', label: '在途车辆', icon: '🚚' },
  { to: '/gov/alerts', label: '告警台账', icon: '⚠️' },
  { to: '/gov/quarantine', label: '证照核验', icon: '📋' },
  { to: '/gov/archives', label: '运输档案', icon: '🔗' },
];

const entNav: NavItem[] = [
  { to: '/enterprise', label: '运输看板', icon: '📈' },
  { to: '/enterprise/orders', label: '运输单管理', icon: '📦' },
  { to: '/enterprise/vehicles', label: '车辆备案', icon: '🚛' },
  { to: '/enterprise/drivers', label: '司机管理', icon: '👤' },
];

/** 跨子域品牌入口（在途 → 官网 / 交易平台），保持生态闭环 */
const SITE_LINKS = [
  { to: 'https://www.yunniuji.com', label: '云牛集官网' },
  { to: 'https://trade.yunniuji.com', label: '牛只交易平台' },
];

function Sidebar({ brand, sub, nav }: { brand: string; sub: string; nav: NavItem[] }) {
  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img src="/logo.svg" onError={(e) => { (e.target as HTMLImageElement).src = "/logo.png"; }} alt="" className="brand-logo" />
        <div>
          <div className="brand-name">{brand}</div>
          <div className="brand-sub">{sub}</div>
        </div>
      </div>
      <div className="sidebar-role">在途监管 · 角色</div>
      <nav className="sidebar-nav">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to.split('/').length === 2}>
            <span className="nav-icon">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-foot">
        <div className="sidebar-site-links">
          {SITE_LINKS.map((s) => (
            <a key={s.to} href={s.to} target="_blank" rel="noreferrer">
              {s.label} ↗
            </a>
          ))}
        </div>
        <div className="sidebar-foot-copy">武汉高灏科技 · 牛只在途监管平台</div>
      </div>
    </aside>
  );
}

function RoleSwitch() {
  const location = useLocation();
  const current = location.pathname.startsWith('/enterprise')
    ? 'enterprise'
    : location.pathname.startsWith('/driver')
      ? 'driver'
      : 'gov';
  return (
    <div className="role-switch">
      <NavLink to={current === 'gov' ? '/gov' : '/gov'} className={current === 'gov' ? 'active' : ''}>
        政府端
      </NavLink>
      <NavLink to={current === 'enterprise' ? '/enterprise' : '/enterprise'} className={current === 'enterprise' ? 'active' : ''}>
        企业端
      </NavLink>
      <NavLink to={current === 'driver' ? '/driver' : '/driver'} className={current === 'driver' ? 'active' : ''}>
        司机端
      </NavLink>
    </div>
  );
}

/** 顶栏用户信息 + 退出（从 localStorage 读取登录用户） */
function TopbarUser({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const [user] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  });

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <div className={`topbar-user${compact ? ' topbar-user--compact' : ''}`}>
      <span className="topbar-user__avatar">👤</span>
      <span className="topbar-user__name">{user.real_name || user.username}</span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={handleLogout}>
        退出
      </button>
    </div>
  );
}

function DesktopShell({ nav, brand, sub, title }: { nav: NavItem[]; brand: string; sub: string; title: string }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div className="shell">
      <div className={`sidebar-mask${sidebarOpen ? ' visible' : ''}`} onClick={() => setSidebarOpen(false)} />
      <div className={`sidebar-wrap${sidebarOpen ? ' open' : ''}`}>
        <Sidebar brand={brand} sub={sub} nav={nav} />
      </div>
      <div className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="topbar-burger"
              aria-label="打开菜单"
              onClick={() => setSidebarOpen(true)}
            >
              ☰
            </button>
            <div className="topbar-title">{title}</div>
          </div>
          <div className="topbar-right">
            <RoleSwitch />
            <TopbarUser />
            <span>🟢 在线</span>
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function MobileShell({ nav, title }: { nav: NavItem[]; title: string }) {
  return (
    <div>
      <header className="mobile-topbar">
        <div className="mt-title">{title}</div>
        <div className="mt-right">
          <RoleSwitch />
          <TopbarUser compact />
          <span className="mt-sites">
            {SITE_LINKS.map((s) => (
              <a key={s.to} href={s.to} target="_blank" rel="noreferrer">
                {s.label}
              </a>
            ))}
          </span>
        </div>
      </header>
      <main style={{ paddingTop: 58 }}>
        <Outlet />
      </main>
      <nav className="mobile-nav">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to.split('/').length === 2}>
            <span className="mn-icon">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** 政府端布局 */
export function GovShell() {
  return <DesktopShell nav={govNav} brand="政府监管驾驶舱" sub="牛只在途监管" title="在途监管 · 政府端" />;
}

/** 企业端布局 */
export function EnterpriseShell() {
  return <DesktopShell nav={entNav} brand="企业运输看板" sub="牛只在途监管" title="在途监管 · 企业端" />;
}

/** 司机端布局（移动端） */
export function DriverShell() {
  const nav: NavItem[] = [
    { to: '/driver', label: '我的任务', icon: '📋' },
    { to: '/driver/orders', label: '任务列表', icon: '🚚' },
  ];
  return <MobileShell nav={nav} title="司机端 · 在途任务" />;
}
